/**
 * Camlin Companion embed widget (doc 116 CO1/CO2 — embed + knowledge +
 * live page context + proactive engagement).
 *
 * Mounts the real avatar SDK widget (`AvatarEmbed`, native mode) and opens a
 * live contextual-chat session with `live.contextualChat.host = 'companion'`
 * (see `liveSession.ts`) so `metadata.host = 'companion'` travels the
 * routing chain documented in `host-contextual-chat.mdc`. Renders its own
 * minimal text transcript/input (the SDK's built-in chat UI is hidden —
 * see `buildCompanionAvatarEmbedConfig`) and drives the session via
 * `AvatarEmbedRef.sendText`, listening for the SDK's `avatar:chat:content`
 * window event for the bot's replies.
 *
 * Layout (reworked after Keith's avatar.* pilot test, 5 Aug 2026; second
 * round 6 Aug 2026):
 *   - ONE always-mounted "avatar stage" hosts the `AvatarEmbed`. Collapsed,
 *     it is the launcher badge circle with a "Chat with our avatar" label
 *     pill beside it; open, the SAME element is restyled as the face pane
 *     across the top of the chat panel. The embed never unmounts on
 *     open/close, so the live session survives (remounting rebuilds config
 *     identity and replays the greeting — the churn loop fixed earlier the
 *     same day). 6 Aug round: the launcher is bigger (76px), frames the
 *     WHOLE head (the embed renders into a fixed 240px inner box that is
 *     CSS-scaled down into the circle), and carries NOTHING over the face
 *     — the SDK's orange AudioPermissionChip that used to float there
 *     (unlabelled AND unclickable through the pointer-events:none wrapper)
 *     is suppressed; every permission ask is a labelled button inside the
 *     open panel.
 *   - The stage shows an explicit loading state while the model streams and
 *     an error+retry state when it fails.
 *   - The transcript auto-scrolls, shows a thinking indicator while a reply
 *     is being generated, and surfaces a stalled/connection-lost status with
 *     reconnect — silence is never the failure mode.
 *
 * Audio (6 Aug 2026 — Keith: "speech doesn't always play"): browsers block
 * playback until a user gesture, so the panel shows an explicit labelled
 * "Enable sound" button whenever sound isn't unlocked yet. Tapping it
 * unlocks (AudioContext resume + element priming via the SDK) and persists
 * the choice (`soundConsent.ts`); on later visits the panel-open click
 * itself is used as the unlock gesture. If playback is blocked mid-session
 * anyway, the SDK's browser-access prompt surfaces here as a visible
 * muted-state chip (never a silent play-fail), plus a small corner dot on
 * the minimized launcher that never covers the face.
 *
 * Voice input (6 Aug 2026 — CO3+ verdict overridden by Keith): a labelled
 * mic button in the panel drives the SAME live-mode path every other
 * avatar SDK consumer uses — `AvatarEmbedRef.startVoiceTurn()` →
 * `useLiveListenControls.handleLivePrimaryButtonClick` → browser mic +
 * Azure STT (token from this host's /talk/get-speech-token). Mic
 * permission is requested on the first tap (never on load), the listening
 * state is visible (pulsing button + caption + interim transcript), and
 * final transcripts land in the chat thread exactly like typed turns
 * (`handleInputExternally` keeps the SDK from double-sending; the widget
 * sends via `sendText` like it does for typed input).
 *
 * Mobile (6 Aug 2026): below the phone breakpoint the open panel is a
 * full-screen takeover with a "Website | Avatar" segmented top bar —
 * Avatar is the full companion (big face + chat + mic), Website minimizes
 * back to the page. The embed stays mounted through the toggle, so no
 * session or transcript state is lost. Tablet+ keeps the floating panel.
 *
 * Round 3 (Keith's live test, 12:07 Sydney 6 Aug 2026):
 *   - Talk while minimized: a small labelled mic button sits ABOVE the
 *     launcher circle (never over the face) so a voice conversation can
 *     run with the panel closed; replies still play aloud and the
 *     accumulated transcript is there when the panel opens.
 *   - The "Camlin Companion" title + close button are a semi-transparent
 *     overlay strip across the top of the face pane (headerOverlay), not
 *     their own row — the freed height goes to the chat. Panel is taller
 *     (720px cap).
 *   - Avatar replies render markdown (markdown.tsx — React elements
 *     only, no innerHTML; links open in a new tab).
 *   - The "Replies are spoken aloud…" hint is dismissible (×) and stays
 *     dismissed (voiceHint.ts).
 *   - Surfaces are opaque (the translucent panel/takeover let the page
 *     bleed through), and the palette follows the HOST page's light/dark
 *     theme (hostTheme.ts) while staying a distinct elevated layer.
 *
 * Consent gating (CO0's consent.ts, hardened CO2) still holds: the live
 * session (and the `AvatarEmbed` mount that opens it) only renders once
 * `isCompanionEngagementAllowed` is true — no session opens before consent
 * — and CO2 adds a periodic re-check so a site consent gate that CLOSES
 * mid-session takes the widget back to inert.
 *
 * CO2 additions (unchanged): page-context digest dispatch on mount and SPA
 * navigation; dwell/scroll/exit-intent proactive triggers behind local +
 * server caps; stable post-consent `visitorId`.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { AvatarEmbed, type AvatarEmbedRef } from '../sdk/AvatarEmbed';
import { getPlaybackAudioContext } from '../core/utils/playbackAudioContext';
import { configureSttEngine } from '../core/utils/sttEngine';
import { CAMLIN_COMPANION_ATTR } from './attributes';
import { CompanionConsentPrompt } from './CompanionConsentPrompt';
import { CompanionNudgeBubble } from './CompanionNudgeBubble';
import {
  chatActionFromAvatarChatContentEvent,
  companionChatReducer,
  type CompanionChatMessage
} from './chatTranscript';
import {
  isCompanionEngagementAllowed,
  isCompanionInert,
  readCompanionConsent,
  writeCompanionConsent,
  type CompanionConsentStorage
} from './consent';
import { isCompanionSoundEnabled, writeCompanionSoundConsent } from './soundConsent';
import { dismissCompanionVoiceHint, isCompanionVoiceHintDismissed } from './voiceHint';
import {
  clampHalfRatio,
  pushHostPage,
  readCompanionHalfRatio,
  writeCompanionHalfRatio
} from './panelLayout';
import { resolveHostTheme, watchHostTheme, type CompanionHostTheme } from './hostTheme';
import { renderCompanionMarkdown } from './markdown';
import { DEFAULT_COMPANION_CONTROL_PLANE_BASE } from './controlPlaneClient';
import { createCompanionEngagementClient } from './engagementClient';
import { fetchCompanionGreeting } from './greetingClient';
import { buildCompanionAvatarEmbedConfig } from './liveSession';
import { capturePageContext, watchPageContext } from './pageContext';
import {
  createProactiveSessionState,
  recordProactiveDismiss,
  type CompanionProactiveSessionState
} from './proactive';
import { evaluateAndShowProactiveNudge } from './proactiveOrchestrator';
import { watchProactiveTriggers } from './proactiveTriggers';
import type { CompanionProactiveTrigger, CompanionSiteConfig } from './types';
import { getOrCreateCompanionVisitorId } from './visitorId';

/** Site consent gate re-check interval — catches an owner's gate closing mid-session (doc 116 §7 D5's "remains inert behind any site consent gate" half). */
const SITE_CONSENT_GATE_POLL_MS = 5000;

/**
 * How long a sent turn may go unanswered before the widget surfaces a
 * stalled-connection status. Companion replies routinely take 10–25s
 * (knowledge_search + LLM), so this must sit well above that.
 */
const REPLY_STALL_TIMEOUT_MS = 45000;

/** Automatic reconnect attempts after an embed/live error before asking the visitor. */
const MAX_AUTO_RECONNECTS = 2;

/**
 * Minimized launcher — round 4 (Keith, 6 Aug 2026 18:04): a larger ROUNDED
 * RECTANGLE, not a circle. Face area on top (nothing ever overlays it),
 * "Chat with avatar" text strip underneath, inside the same rect.
 */
const LAUNCHER_W_PX = 104;
const LAUNCHER_FACE_PX = 96;
const LAUNCHER_LABEL_PX = 30;
const LAUNCHER_RADIUS_PX = 20;
const EDGE_GAP_PX = 20;

/**
 * Round 6 (Keith): the minimized launcher scales UP at desktop
 * breakpoints — phones and tablets keep the round-4 geometry. 1024px is
 * deliberately above the 768px tablet class; the face stays unobstructed
 * (pure geometry scale, nothing new over it).
 */
const DESKTOP_MEDIA_QUERY = '(min-width: 1024px)';
const LAUNCHER_DESKTOP_SCALE = 1.45;

interface LauncherGeometry {
  w: number;
  face: number;
  label: number;
  h: number;
  radius: number;
  labelFontPx: number;
  miniMic: number;
}

function launcherGeometry(desktopViewport: boolean): LauncherGeometry {
  const scale = desktopViewport ? LAUNCHER_DESKTOP_SCALE : 1;
  const w = Math.round(LAUNCHER_W_PX * scale);
  const face = Math.round(LAUNCHER_FACE_PX * scale);
  const label = Math.round(LAUNCHER_LABEL_PX * scale);
  return {
    w,
    face,
    label,
    h: face + label,
    radius: Math.round(LAUNCHER_RADIUS_PX * scale),
    labelFontPx: desktopViewport ? 13 : 11,
    miniMic: Math.round(MINI_MIC_SIZE_PX * (desktopViewport ? 1.2 : 1))
  };
}
/**
 * Fixed square the embed renders into while minimized; CSS-scaled down to
 * the launcher circle. 240px at cameraZoom 760 ≈ a 32cm world band — the
 * whole head — regardless of how small the circle itself is.
 */
const STAGE_INNER_PX = 240;
const FACE_HEIGHT_PX = 220;
const PANEL_WIDTH = 'min(350px, calc(100vw - 32px))';
/** Round 3 (Keith): taller again — 640 → 720 cap, tighter viewport margin. */
const PANEL_HEIGHT = 'min(720px, calc(100vh - 64px))';

/**
 * Desktop panel size modes — round 4 (Keith, 6 Aug 2026 18:04):
 *   float — the classic floating panel (default, unchanged geometry)
 *   tall  — maximized to a full-height side panel on the right edge
 *   half  — the avatar takes the right HALF of the screen, the website
 *           keeps the left half (flush, no gaps; bigger face pane)
 * Phone keeps the existing full-screen takeover; these modes are
 * desktop/tablet only. The stage + panel are the same always-mounted
 * elements in every mode (style-only changes), so the live session and
 * transcript survive every mode switch.
 */
type CompanionPanelMode = 'float' | 'tall' | 'half';
const TALL_PANEL_WIDTH = 'min(380px, calc(100vw - 32px))';
const HALF_PANEL_WIDTH = '50vw';
const TALL_FACE_HEIGHT = '260px';
const HALF_FACE_HEIGHT = 'min(42vh, 460px)';

function panelFaceHeight(mode: CompanionPanelMode): string {
  if (mode === 'half') return HALF_FACE_HEIGHT;
  if (mode === 'tall') return TALL_FACE_HEIGHT;
  return `${FACE_HEIGHT_PX}px`;
}
/** Small standalone mic button above the minimized launcher (round 3). */
const MINI_MIC_SIZE_PX = 38;
/** Height of the mobile takeover's "Website | Avatar" segmented top bar. */
const TAKEOVER_BAR_PX = 52;
/** Face pane height inside the mobile takeover — big face, chat below. */
const PHONE_FACE_HEIGHT = 'min(34vh, 300px)';
/**
 * Phone breakpoint for the full-screen takeover. 700px keeps 768px
 * tablets on the floating panel while real phones (≤ ~430px, plus small
 * landscape phones) get the takeover.
 */
const PHONE_MEDIA_QUERY = '(max-width: 699px)';

export interface CompanionWidgetProps {
  config: CompanionSiteConfig;
  storage?: CompanionConsentStorage;
  /** Base URL for the CO2 engagement control-plane calls — defaults to the same not-yet-hosted control plane the site config fetch targets. */
  controlPlaneBaseUrl?: string;
  onReady?: () => void;
  onConsentChange?: (accepted: boolean) => void;
}

const FONT_STACK =
  'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

/**
 * Theme palettes (round 3): the panel follows the HOST page's light/dark
 * theme (hostTheme.ts) but is always a distinct elevated layer — opaque
 * surface, a shade off the page background, with an elevation shadow.
 * Every surface color here is OPAQUE by design: the old rgba(…,0.94)
 * panel let the page bleed through (Keith flagged it on the phone
 * takeover first, then generally in round 3).
 */
interface CompanionPalette {
  panelBg: string;
  panelBorder: string;
  panelShadow: string;
  text: string;
  subtle: string;
  assistantBubbleBg: string;
  userBubbleBg: string;
  interimBubbleBg: string;
  inputBg: string;
  inputBorder: string;
  barBorder: string;
  segmentTrack: string;
  segmentIdleText: string;
  pillBg: string;
  pillText: string;
  pillShadow: string;
  statusText: string;
  statusBorder: string;
  micIdleBg: string;
  micIdleBorder: string;
  closeColor: string;
}

const DARK_PALETTE: CompanionPalette = {
  panelBg: '#111827',
  panelBorder: '1px solid rgba(255,255,255,0.09)',
  panelShadow: '0 16px 48px rgba(0,0,0,0.55)',
  text: '#e2e8f0',
  subtle: 'rgba(226,232,240,0.55)',
  assistantBubbleBg: '#1f2937',
  userBubbleBg: '#4f46e5',
  interimBubbleBg: '#3730a3',
  inputBg: '#1f2937',
  inputBorder: '1px solid rgba(255,255,255,0.15)',
  barBorder: '1px solid rgba(255,255,255,0.08)',
  segmentTrack: 'rgba(255,255,255,0.08)',
  segmentIdleText: 'rgba(226,232,240,0.75)',
  pillBg: '#111827',
  pillText: '#e2e8f0',
  pillShadow: '0 8px 24px rgba(0,0,0,0.45)',
  statusText: '#fbbf24',
  statusBorder: '1px solid rgba(251,191,36,0.6)',
  micIdleBg: '#1f2937',
  micIdleBorder: '1px solid rgba(255,255,255,0.2)',
  closeColor: 'rgba(226,232,240,0.85)'
};

const LIGHT_PALETTE: CompanionPalette = {
  panelBg: '#ffffff',
  panelBorder: '1px solid rgba(15,23,42,0.1)',
  panelShadow: '0 16px 48px rgba(15,23,42,0.22)',
  text: '#0f172a',
  subtle: 'rgba(15,23,42,0.55)',
  assistantBubbleBg: '#eef2f7',
  userBubbleBg: '#4f46e5',
  interimBubbleBg: '#a5b4fc',
  inputBg: '#f8fafc',
  inputBorder: '1px solid rgba(15,23,42,0.18)',
  barBorder: '1px solid rgba(15,23,42,0.08)',
  segmentTrack: 'rgba(15,23,42,0.07)',
  segmentIdleText: 'rgba(15,23,42,0.65)',
  pillBg: '#ffffff',
  pillText: '#0f172a',
  pillShadow: '0 8px 24px rgba(15,23,42,0.25)',
  statusText: '#b45309',
  statusBorder: '1px solid rgba(180,83,9,0.5)',
  micIdleBg: '#f1f5f9',
  micIdleBorder: '1px solid rgba(15,23,42,0.2)',
  closeColor: 'rgba(255,255,255,0.9)'
};

function paletteFor(theme: CompanionHostTheme): CompanionPalette {
  return theme === 'dark' ? DARK_PALETTE : LIGHT_PALETTE;
}

/**
 * The one persistent home of the `AvatarEmbed`: launcher badge circle when
 * the panel is closed, face pane across the top of the panel when open
 * (desktop float or mobile takeover). Only STYLE changes between shapes —
 * the element (and the embed inside it) stays mounted, so the live session
 * is never torn down by an open/close or a Website/Avatar toggle.
 */
function buildStageStyle(
  panelOpen: boolean,
  phoneViewport: boolean,
  panelMode: CompanionPanelMode,
  launcher: LauncherGeometry,
  halfWidth: string = HALF_PANEL_WIDTH
): React.CSSProperties {
  const shared: React.CSSProperties = {
    position: 'fixed',
    overflow: 'hidden',
    background: 'linear-gradient(160deg, #1e1b4b 0%, #0f172a 60%, #082f49 100%)'
  };

  if (!panelOpen) {
    // Face area of the rounded-rect launcher (round 4; desktop-scaled
    // geometry round 6). The "Chat with avatar" strip renders as its own
    // element directly below — the face itself carries nothing.
    return {
      ...shared,
      bottom: `${EDGE_GAP_PX + launcher.label}px`,
      right: `${EDGE_GAP_PX}px`,
      width: `${launcher.w}px`,
      height: `${launcher.face}px`,
      borderRadius: `${launcher.radius}px ${launcher.radius}px 0 0`,
      boxShadow: '0 8px 24px rgba(79,70,229,0.35)',
      zIndex: 9998
    };
  }

  if (phoneViewport) {
    // Mobile takeover: face pane spans the full width, directly under the
    // "Website | Avatar" top bar (which is the first TAKEOVER_BAR_PX of the
    // full-screen panel below this stage).
    return {
      ...shared,
      top: `${TAKEOVER_BAR_PX}px`,
      left: 0,
      right: 0,
      height: PHONE_FACE_HEIGHT,
      borderRadius: 0,
      zIndex: 10000
    };
  }

  if (panelMode === 'half') {
    return {
      ...shared,
      top: 0,
      right: 0,
      width: halfWidth,
      height: HALF_FACE_HEIGHT,
      borderRadius: 0,
      zIndex: 10000
    };
  }

  if (panelMode === 'tall') {
    return {
      ...shared,
      top: `${EDGE_GAP_PX}px`,
      right: `${EDGE_GAP_PX}px`,
      width: TALL_PANEL_WIDTH,
      height: TALL_FACE_HEIGHT,
      borderRadius: '16px 16px 0 0',
      zIndex: 10000
    };
  }

  return {
    ...shared,
    right: `${EDGE_GAP_PX}px`,
    // Exactly covers the top FACE_HEIGHT of the panel below it.
    bottom: `calc(${EDGE_GAP_PX}px + ${PANEL_HEIGHT} - ${FACE_HEIGHT_PX}px)`,
    width: PANEL_WIDTH,
    height: `${FACE_HEIGHT_PX}px`,
    borderRadius: '16px 16px 0 0',
    zIndex: 10000
  };
}

/**
 * Inner wrapper the embed actually lives in. Open: fills the stage (face
 * pane). Collapsed: a fixed 240px square CSS-scaled down into the launcher
 * circle so the whole head stays framed (Keith: "too zoomed in" at the old
 * direct-render). The wrapper exists in BOTH modes so React never remounts
 * the embed (a remount tears down the live session).
 */
function buildStageInnerStyle(panelOpen: boolean, launcher: LauncherGeometry): React.CSSProperties {
  if (panelOpen) {
    return { position: 'absolute', inset: 0 };
  }

  const scale = launcher.w / STAGE_INNER_PX;
  return {
    position: 'absolute',
    left: '50%',
    top: '50%',
    width: `${STAGE_INNER_PX}px`,
    height: `${STAGE_INNER_PX}px`,
    transform: `translate(-50%, -50%) scale(${scale})`
  };
}

const stageEmbedStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  // Companion owns all interaction; the avatar is presentation-only here
  // (native mode wires OrbitControls, which would otherwise steal
  // clicks/drags from the launcher button and page scroll).
  pointerEvents: 'none'
};

const stageOverlayStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  // Above the SDK's own positioned chrome (LiveVoiceOverlay's error chip
  // uses zIndex 10) so the loading/error state reads as ONE surface.
  zIndex: 20,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  background: 'linear-gradient(160deg, rgba(30,27,75,0.94), rgba(15,23,42,0.94))',
  color: '#e2e8f0',
  fontFamily: FONT_STACK,
  fontSize: '12px',
  textAlign: 'center',
  padding: '8px'
};

/** Click target covering the WHOLE launcher rect (face area + label strip). */
function buildLauncherButtonStyle(launcher: LauncherGeometry): React.CSSProperties {
  return {
    position: 'fixed',
    bottom: `${EDGE_GAP_PX}px`,
    right: `${EDGE_GAP_PX}px`,
    width: `${launcher.w}px`,
    height: `${launcher.h}px`,
    borderRadius: `${launcher.radius}px`,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    zIndex: 9999,
    padding: 0,
    // Fixed footprint — no layout shift before interaction
    contain: 'layout style'
  };
}

/**
 * "Chat with avatar" strip under the launcher face (round 4) — part of the
 * same rounded rect, never over the face. Opaque, matches the stage scene's
 * dark bottom so the rect reads as one piece.
 */
function buildLauncherTextStripStyle(launcher: LauncherGeometry): React.CSSProperties {
  return {
    position: 'fixed',
    bottom: `${EDGE_GAP_PX}px`,
    right: `${EDGE_GAP_PX}px`,
    width: `${launcher.w}px`,
    height: `${launcher.label}px`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#0b1120',
    color: '#e2e8f0',
    fontFamily: FONT_STACK,
    fontSize: `${launcher.labelFontPx}px`,
    fontWeight: 600,
    lineHeight: 1,
    whiteSpace: 'nowrap',
    borderRadius: `0 0 ${launcher.radius}px ${launcher.radius}px`,
    boxShadow: '0 8px 24px rgba(79,70,229,0.35)',
    zIndex: 9998,
    pointerEvents: 'none'
  };
}

/**
 * Thinking indicator for the MINIMIZED launcher (round 4, Keith item 7):
 * the same pulsing-dots effect the in-panel chat bubble uses, as a small
 * bubble beside the launcher — near the face, never over it.
 */
function buildLauncherThinkingStyle(palette: CompanionPalette, launcher: LauncherGeometry): React.CSSProperties {
  return {
    position: 'fixed',
    bottom: `${EDGE_GAP_PX + Math.round(launcher.h / 2) - 14}px`,
    right: `${EDGE_GAP_PX + launcher.w + 10}px`,
    zIndex: 9998,
    background: palette.assistantBubbleBg,
    color: palette.text,
    borderRadius: '12px 12px 2px 12px',
    padding: '7px 12px',
    fontFamily: FONT_STACK,
    fontSize: '13px',
    lineHeight: 1,
    boxShadow: palette.pillShadow,
    pointerEvents: 'none'
  };
}

/**
 * Talk-while-minimized mic (round 3): a small labelled circle ABOVE the
 * launcher — same never-over-the-face rule as everything else. Tapping it
 * runs the exact same voice turn as the panel's mic button; replies play
 * aloud and the transcript accumulates for whenever the panel opens.
 */
function buildMiniMicStyle(
  palette: CompanionPalette,
  listening: boolean,
  enabled: boolean,
  launcher: LauncherGeometry
): React.CSSProperties {
  return {
    position: 'fixed',
    bottom: `${EDGE_GAP_PX + launcher.h + 8}px`,
    right: `${EDGE_GAP_PX + Math.round((launcher.w - launcher.miniMic) / 2)}px`,
    width: `${launcher.miniMic}px`,
    height: `${launcher.miniMic}px`,
    borderRadius: '999px',
    border: listening ? 'none' : palette.micIdleBorder,
    background: listening ? '#dc2626' : palette.micIdleBg,
    color: listening ? '#fff' : palette.pillText,
    fontSize: '16px',
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: enabled ? 'pointer' : 'default',
    opacity: enabled || listening ? 1 : 0.55,
    boxShadow: palette.pillShadow,
    zIndex: 9999,
    padding: 0
  };
}

/**
 * Muted-state indicator for the MINIMIZED launcher: a small dot pinned to
 * the circle's bottom-right rim — never over the face (Keith, 6 Aug 2026:
 * nothing floats over the minimized face). Indicator only; the labelled
 * un-mute affordance lives inside the open panel.
 */
const mutedDotStyle: React.CSSProperties = {
  position: 'fixed',
  bottom: `${EDGE_GAP_PX - 4}px`,
  right: `${EDGE_GAP_PX - 4}px`,
  width: '22px',
  height: '22px',
  borderRadius: '999px',
  background: '#b45309',
  color: '#fff',
  fontSize: '11px',
  lineHeight: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '2px solid rgba(15,23,42,0.92)',
  zIndex: 10001,
  pointerEvents: 'none'
};

function buildPanelStyle(
  phoneViewport: boolean,
  palette: CompanionPalette,
  panelMode: CompanionPanelMode,
  halfWidth: string = HALF_PANEL_WIDTH
): React.CSSProperties {
  const shared: React.CSSProperties = {
    position: 'fixed',
    color: palette.text,
    zIndex: 9999,
    fontFamily: FONT_STACK,
    fontSize: '14px',
    display: 'flex',
    flexDirection: 'column',
    // Opaque by design (round 3): the old rgba backgrounds let the host
    // page bleed through the chat area and the phone takeover.
    background: palette.panelBg
  };

  if (phoneViewport) {
    return {
      ...shared,
      inset: 0,
      width: '100%',
      height: '100%',
      borderRadius: 0
    };
  }

  if (panelMode === 'half') {
    // Avatar takes the right share of the screen (divider-adjustable,
    // round 5 item 7), the website keeps the rest — and is pushed out
    // from under the panel, not overlaid (item 6).
    return {
      ...shared,
      top: 0,
      right: 0,
      bottom: 0,
      width: halfWidth,
      borderRadius: 0,
      borderLeft: palette.panelBorder,
      boxShadow: palette.panelShadow
    };
  }

  if (panelMode === 'tall') {
    // Maximized: full-height side panel on the right edge.
    return {
      ...shared,
      top: `${EDGE_GAP_PX}px`,
      bottom: `${EDGE_GAP_PX}px`,
      right: `${EDGE_GAP_PX}px`,
      width: TALL_PANEL_WIDTH,
      borderRadius: '16px',
      border: palette.panelBorder,
      boxShadow: palette.panelShadow
    };
  }

  return {
    ...shared,
    bottom: `${EDGE_GAP_PX}px`,
    right: `${EDGE_GAP_PX}px`,
    width: PANEL_WIDTH,
    height: PANEL_HEIGHT,
    borderRadius: '16px',
    border: palette.panelBorder,
    boxShadow: palette.panelShadow
  };
}

function buildTakeoverBarStyle(palette: CompanionPalette): React.CSSProperties {
  return {
    height: `${TAKEOVER_BAR_PX}px`,
    flex: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: palette.panelBg,
    borderBottom: palette.barBorder
  };
}

function buildSegmentWrapStyle(palette: CompanionPalette): React.CSSProperties {
  return {
    display: 'flex',
    background: palette.segmentTrack,
    borderRadius: '999px',
    padding: '3px',
    gap: '2px'
  };
}

function buildSegmentStyle(active: boolean, palette: CompanionPalette): React.CSSProperties {
  return {
    padding: '7px 20px',
    borderRadius: '999px',
    border: 'none',
    fontFamily: FONT_STACK,
    fontWeight: 600,
    fontSize: '13px',
    lineHeight: 1.2,
    background: active ? 'linear-gradient(135deg, #4f46e5, #06b6d4)' : 'transparent',
    color: active ? '#fff' : palette.segmentIdleText,
    cursor: 'pointer'
  };
}

const panelBodyStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  padding: '10px 12px 12px',
  minHeight: 0
};

/**
 * Round 3: the title + close live as a semi-transparent overlay strip
 * across the TOP OF THE FACE PANE (inside the stage element), not as
 * their own row in the panel body — the freed height goes to the chat.
 * The face scene is dark in both themes, so the strip is a dark scrim
 * with white text regardless of the host theme.
 */
const headerOverlayStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: '42px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  padding: '0 8px 0 14px',
  background: 'linear-gradient(180deg, rgba(2,6,23,0.62) 0%, rgba(2,6,23,0.35) 65%, rgba(2,6,23,0) 100%)',
  color: '#fff',
  fontFamily: FONT_STACK,
  fontSize: '14px',
  fontWeight: 700,
  zIndex: 30,
  pointerEvents: 'none'
};

const headerCloseStyle: React.CSSProperties = {
  border: 'none',
  background: 'rgba(2,6,23,0.35)',
  color: 'rgba(255,255,255,0.92)',
  cursor: 'pointer',
  fontSize: '15px',
  lineHeight: 1,
  padding: '6px 8px',
  borderRadius: '999px',
  pointerEvents: 'auto'
};

/** Panel size-mode buttons in the header overlay (round 4, item 10). */
function buildHeaderModeButtonStyle(active: boolean): React.CSSProperties {
  return {
    ...headerCloseStyle,
    fontSize: '13px',
    background: active ? 'rgba(79,70,229,0.75)' : 'rgba(2,6,23,0.35)'
  };
}

/**
 * Monochrome mic glyph (round 4, Keith item 6): a plain stroke SVG that
 * inherits `currentColor` from its button, so it follows the widget's
 * light/dark palette (and reads as part of the host site's icon language)
 * instead of the multi-coloured emoji it replaces.
 */
function MicIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="9" y="2" width="6" height="12" rx="3" />
      <path d="M5 10a7 7 0 0 0 14 0" />
      <line x1="12" y1="19" x2="12" y2="22" />
    </svg>
  );
}

/** Labelled first-visit "Enable sound" affordance — a real button, not an icon mystery. */
const soundBannerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  width: '100%',
  border: 'none',
  borderRadius: '10px',
  padding: '9px 12px',
  margin: '2px 0 4px',
  background: 'linear-gradient(135deg, #4f46e5, #06b6d4)',
  color: '#fff',
  fontFamily: FONT_STACK,
  fontSize: '13px',
  fontWeight: 700,
  cursor: 'pointer'
};

const transcriptStyle: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  margin: '8px 0',
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  minHeight: 0
};

function buildUserBubbleStyle(palette: CompanionPalette): React.CSSProperties {
  return {
    alignSelf: 'flex-end',
    background: palette.userBubbleBg,
    color: '#fff',
    borderRadius: '12px 12px 2px 12px',
    padding: '6px 10px',
    maxWidth: '85%'
  };
}

function buildAssistantBubbleStyle(palette: CompanionPalette): React.CSSProperties {
  return {
    alignSelf: 'flex-start',
    background: palette.assistantBubbleBg,
    color: palette.text,
    borderRadius: '12px 12px 12px 2px',
    padding: '6px 10px',
    maxWidth: '85%',
    overflowWrap: 'break-word'
  };
}

/** In-flight voice transcript, shown while the mic is live. */
function buildInterimBubbleStyle(palette: CompanionPalette): React.CSSProperties {
  return {
    ...buildUserBubbleStyle(palette),
    background: palette.interimBubbleBg,
    fontStyle: 'italic'
  };
}

function buildStatusLineStyle(palette: CompanionPalette): React.CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    color: palette.statusText,
    margin: '0 0 6px'
  };
}

function buildStatusActionStyle(palette: CompanionPalette): React.CSSProperties {
  return {
    border: palette.statusBorder,
    borderRadius: '999px',
    background: 'transparent',
    color: palette.statusText,
    fontSize: '12px',
    fontWeight: 600,
    padding: '3px 10px',
    cursor: 'pointer',
    whiteSpace: 'nowrap'
  };
}

const inputRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: '8px'
};

function buildInputStyle(palette: CompanionPalette): React.CSSProperties {
  return {
    flex: 1,
    minWidth: 0,
    borderRadius: '8px',
    border: palette.inputBorder,
    background: palette.inputBg,
    color: palette.text,
    padding: '8px 10px',
    fontSize: '14px'
  };
}

/**
 * Chest mic (round 6, Keith verbatim): the microphone button lives inside
 * the avatar panel, just below the face but WITHIN the face pane —
 * overlaid on the avatar's chest area. Primary voice affordance when the
 * panel is open; it wears the same capture-truth voice phases the 2.5.x
 * strip established (idle / starting / listening / processing / done),
 * and the slimmed strip below the transcript keeps the labelled state +
 * live level bars visible.
 */
const CHEST_MIC_SIZE_PX = 56;

function buildChestMicStyle(phase: CompanionVoicePhase, enabled: boolean): React.CSSProperties {
  const background = phase === 'listening'
    ? '#dc2626'
    : phase === 'starting' || phase === 'processing'
      ? '#b45309'
      : phase === 'done'
        ? '#15803d'
        : 'rgba(15,23,42,0.62)';
  return {
    position: 'absolute',
    left: '50%',
    bottom: '12px',
    transform: 'translateX(-50%)',
    width: `${CHEST_MIC_SIZE_PX}px`,
    height: `${CHEST_MIC_SIZE_PX}px`,
    borderRadius: '999px',
    border: phase === 'idle' ? '1.5px solid rgba(226,232,240,0.5)' : 'none',
    background,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: enabled ? 'pointer' : 'default',
    opacity: enabled || phase !== 'idle' ? 1 : 0.55,
    boxShadow: '0 6px 20px rgba(2,6,23,0.45)',
    // Above the embed + loading overlay (z20), below the header strip.
    zIndex: 25,
    padding: 0,
    pointerEvents: 'auto'
  };
}

const sendButtonStyle: React.CSSProperties = {
  borderRadius: '8px',
  border: 'none',
  background: 'linear-gradient(135deg, #4f46e5, #06b6d4)',
  color: '#fff',
  fontWeight: 700,
  padding: '8px 14px',
  cursor: 'pointer',
  flex: 'none'
};

function buildVoiceCaptionStyle(palette: CompanionPalette): React.CSSProperties {
  return {
    margin: '6px 0 0',
    fontSize: '11px',
    textAlign: 'center',
    color: palette.subtle,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px'
  };
}

/**
 * Voice turn state strip (round 4, Keith item 8): an unmistakable, labelled
 * state indicator above the input row — idle (hidden) / starting /
 * listening (red, live level bars) / processing (amber, spinner) / done
 * (green flash). Replaces "a subtle pulse you can miss" as the only signal.
 */
type CompanionVoicePhase = 'idle' | 'starting' | 'listening' | 'processing' | 'done';

function buildVoiceStateStripStyle(phase: CompanionVoicePhase, palette: CompanionPalette): React.CSSProperties {
  const tint = phase === 'listening'
    ? { background: 'rgba(220,38,38,0.14)', border: '1px solid rgba(220,38,38,0.55)', color: '#dc2626' }
    : phase === 'processing'
      ? { background: 'rgba(217,119,6,0.12)', border: '1px solid rgba(217,119,6,0.5)', color: '#b45309' }
      : phase === 'done'
        ? { background: 'rgba(22,163,74,0.12)', border: '1px solid rgba(22,163,74,0.5)', color: '#15803d' }
        : { background: palette.inputBg, border: palette.inputBorder, color: palette.text };
  // Slimmed in round 6: the chest mic carries the primary affordance now,
  // so the strip is a compact labelled status line (still every phase,
  // still the live level bars — capture truth stays visible).
  return {
    display: 'flex',
    alignItems: 'center',
    gap: '7px',
    borderRadius: '8px',
    padding: '4px 9px',
    margin: '0 0 6px',
    fontSize: '11.5px',
    fontWeight: 600,
    fontFamily: FONT_STACK,
    ...tint
  };
}

const voiceLevelBarsStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '2px',
  height: '18px',
  flex: 'none'
};

/** The × on the dismissible "Replies are spoken aloud…" hint (round 3). */
function buildHintDismissStyle(palette: CompanionPalette): React.CSSProperties {
  return {
    border: 'none',
    background: 'transparent',
    color: palette.subtle,
    cursor: 'pointer',
    fontSize: '13px',
    lineHeight: 1,
    padding: '2px 4px'
  };
}

/**
 * Inline styles cannot express keyframes; this small sheet carries the
 * loading spinner + thinking-dots + mic-pulse animations. Rendered inside
 * the widget so it works in both the shadow-root (custom element) and
 * body-mount paths without touching the host page's stylesheets.
 */
const WIDGET_KEYFRAMES = `
@keyframes camlin-companion-spin { to { transform: rotate(360deg); } }
@keyframes camlin-companion-pulse { 0%, 80%, 100% { opacity: 0.25; } 40% { opacity: 1; } }
@keyframes camlin-companion-mic-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.55); }
  50% { box-shadow: 0 0 0 7px rgba(239,68,68,0); }
}
.camlin-companion-spinner {
  width: 18px; height: 18px; border-radius: 999px;
  border: 2px solid rgba(226,232,240,0.25); border-top-color: #e2e8f0;
  animation: camlin-companion-spin 0.9s linear infinite;
}
.camlin-companion-dot { display: inline-block; animation: camlin-companion-pulse 1.2s infinite; }
.camlin-companion-dot:nth-child(2) { animation-delay: 0.2s; }
.camlin-companion-dot:nth-child(3) { animation-delay: 0.4s; }
.camlin-companion-mic-live { animation: camlin-companion-mic-pulse 1.4s ease-out infinite; }
@keyframes camlin-companion-level { 0%, 100% { transform: scaleY(0.35); } 50% { transform: scaleY(1); } }
.camlin-companion-level-bar {
  display: inline-block; width: 3px; height: 16px; border-radius: 2px;
  background: currentColor; transform-origin: center;
  animation: camlin-companion-level 1s ease-in-out infinite;
}
.camlin-companion-level-bar:nth-child(2) { animation-delay: 0.15s; }
.camlin-companion-level-bar:nth-child(3) { animation-delay: 0.3s; }
.camlin-companion-level-bar:nth-child(4) { animation-delay: 0.45s; }
.camlin-companion-level-bar:nth-child(5) { animation-delay: 0.6s; }
`;

type CompanionConnectionState = 'ok' | 'stalled' | 'reconnecting' | 'error';

type CompanionMicState = 'idle' | 'starting' | 'listening';

interface CompanionBrowserAccessPrompt {
  kind: 'audio' | 'microphone';
  title: string;
  message: string;
}

function defaultStorage(): CompanionConsentStorage {
  return window.localStorage;
}

function isPlaybackContextRunning(): boolean {
  return getPlaybackAudioContext()?.state === 'running';
}

export function CompanionWidget({
  config,
  storage = defaultStorage(),
  controlPlaneBaseUrl = DEFAULT_COMPANION_CONTROL_PLANE_BASE,
  onReady,
  onConsentChange
}: CompanionWidgetProps) {
  const [consentAccepted, setConsentAccepted] = useState(() =>
    isCompanionEngagementAllowed(storage)
  );
  const [panelOpen, setPanelOpen] = useState(false);
  const [messages, setMessages] = useState<CompanionChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [avatarReady, setAvatarReady] = useState(false);
  const [embedError, setEmbedError] = useState<string | null>(null);
  const [embedEpoch, setEmbedEpoch] = useState(0);
  const [awaitingReply, setAwaitingReply] = useState(false);
  const [connectionState, setConnectionState] = useState<CompanionConnectionState>('ok');
  const [phoneViewport, setPhoneViewport] = useState<boolean>(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(PHONE_MEDIA_QUERY).matches
      : false
  );
  // Round 6 item 1: bigger minimized launcher at desktop breakpoints.
  const [desktopViewport, setDesktopViewport] = useState<boolean>(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(DESKTOP_MEDIA_QUERY).matches
      : false
  );
  // True once audio playback has been unlocked THIS page load (the
  // persisted choice in soundConsent.ts only skips the ask — the technical
  // unlock still needs a gesture per browser policy).
  const [soundOn, setSoundOn] = useState<boolean>(() => isPlaybackContextRunning());
  const [browserPrompt, setBrowserPrompt] = useState<CompanionBrowserAccessPrompt | null>(null);
  const [micState, setMicState] = useState<CompanionMicState>('idle');
  const [interimTranscript, setInterimTranscript] = useState('');
  // Desktop panel size mode (round 4, item 10). Phone always takes over.
  const [panelMode, setPanelMode] = useState<CompanionPanelMode>('float');
  // Half-mode split ratio (round 5 item 7): panel fraction of the viewport,
  // divider-draggable, persisted across visits.
  const [halfRatio, setHalfRatio] = useState<number>(() => readCompanionHalfRatio(storage));
  const dividerDraggingRef = useRef(false);
  // Voice phase bookkeeping (round 4, item 8): whether the reply being
  // awaited came from a voice turn, and the transient "done" flash after
  // it lands.
  const lastTurnVoiceRef = useRef(false);
  const [voiceDoneFlash, setVoiceDoneFlash] = useState(false);
  // Capture-truth tracking (round 5 item 2): true while the ASR engine says
  // samples are actually being buffered; a recognize-in-flight window shows
  // as voiceProcessing between capture end and the reply send.
  const captureActiveRef = useRef(false);
  const [voiceProcessing, setVoiceProcessing] = useState(false);
  // True once the visitor has taken a turn (typed or spoken) — the guard
  // for the SPA-nav greeting refresh (round 6 item 5): a pending greeting
  // may be swapped, a begun conversation must never be rewritten.
  const conversationStartedRef = useRef(false);
  // Host page light/dark theme — resolved from the page's own theme
  // markers / background, falling back to prefers-color-scheme (round 3).
  const [hostTheme, setHostTheme] = useState<CompanionHostTheme>(() => resolveHostTheme());
  // "Replies are spoken aloud…" hint dismissal — persisted (round 3).
  const [voiceHintDismissed, setVoiceHintDismissed] = useState<boolean>(() =>
    isCompanionVoiceHintDismissed(storage)
  );
  const [pagePath, setPagePath] = useState<string>(() =>
    typeof window !== 'undefined' ? window.location.pathname : '/'
  );
  const [nudge, setNudge] = useState<{ trigger: CompanionProactiveTrigger } | null>(null);
  const readyEmitted = useRef(false);
  const avatarEmbedRef = useRef<AvatarEmbedRef | null>(null);
  /**
   * Messages sent while the embed is between live sessions (6 Aug 2026
   * Companion v4 pass): a remount (reconnect / enable-sound restart)
   * replaces the whole LiveModeClient, so even the client-level pre-link
   * queue in liveMode.ts dies with the old instance. Anything sent while
   * `avatarReady` is false lands here and is flushed through `sendText`
   * when the new embed reports ready.
   */
  const pendingSendsRef = useRef<string[]>([]);
  /**
   * True while the browser's mic permission dialog is outstanding for a
   * turn this widget started (item 9). Set by `avatar:voice-turn-pending`,
   * cleared when listening actually starts (or by the 60s valve). While
   * set, handleMicTap's finally must NOT reset the button to idle.
   */
  const micPendingGrantRef = useRef(false);
  const transcriptRef = useRef<HTMLDivElement | null>(null);
  const autoReconnectsRef = useRef(0);
  const autoReconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const visitorIdRef = useRef<string | null>(null);
  const proactiveSessionRef = useRef<CompanionProactiveSessionState>(createProactiveSessionState());
  const engagementClientRef = useRef(
    createCompanionEngagementClient({ baseUrl: controlPlaneBaseUrl })
  );

  const inert = isCompanionInert(storage);

  // Mint (and persist) the stable visitor id lazily, ONLY once the widget
  // is no longer inert — doc 116 §7 D5's "before first engagement/
  // transcript processing" applies to this id too, since its only uses
  // are transcript retention and engagement-cap bookkeeping. Minted BEFORE
  // the embed-config memo below so the first non-inert render already
  // carries it into the session metadata.
  if (!inert && visitorIdRef.current === null) {
    visitorIdRef.current = getOrCreateCompanionVisitorId(storage);
  }

  // MUST be referentially stable across re-renders: the embed's live
  // session hooks key their open/teardown effects on config identity, so
  // rebuilding this object inline in the JSX made every transcript update
  // (setMessages on each bot reply) restart the live session — greeting →
  // re-render → new config → session teardown/reopen → greeting again, a
  // session-churn loop observed live on the avatar.* pilot (5 Aug 2026,
  // ~20 sessions/45s). Keyed on `inert` so the visitor id minted above is
  // captured once consent exists. `embedEpoch` changes ONLY on deliberate
  // reconnect/retry — the one case where a session restart is the point.
  const avatarEmbedConfig = useMemo(
    () => buildCompanionAvatarEmbedConfig(config, {
      visitorId: visitorIdRef.current ?? undefined,
      // Round 6 item 3 — THE one-line page-context fix: seed the initial
      // digest INTO the session-open metadata. The mount-time
      // `avatar:live:contextUpdate` dispatch fires before the SDK's
      // window-event listener exists, so without this every session
      // opened as page=(none) and per-page greetings never resolved
      // (verified in prod logs, 6 Aug 2026).
      pageContext: typeof document !== 'undefined' ? capturePageContext() : undefined
    }),
    [config, inert, embedEpoch]
  );

  /** Deliberate embed remount: new live session, fresh model load. Transcript is kept; the replayed greeting is deduped by the reducer. */
  const reconnect = useCallback(() => {
    if (autoReconnectTimerRef.current) {
      clearTimeout(autoReconnectTimerRef.current);
      autoReconnectTimerRef.current = null;
    }
    setAvatarReady(false);
    setEmbedError(null);
    setAwaitingReply(false);
    setMicState('idle');
    setInterimTranscript('');
    setConnectionState('reconnecting');
    setEmbedEpoch((epoch) => epoch + 1);
  }, []);

  const handleEmbedReady = useCallback(() => {
    setAvatarReady(true);
    setEmbedError(null);
    autoReconnectsRef.current = 0;
    setConnectionState((state) => (state === 'reconnecting' ? 'ok' : state));
    // Deliver anything typed/spoken while the embed was between sessions.
    // sendText lands in the (possibly brand-new) LiveModeClient, whose own
    // pre-link queue holds it the last few ms until `visual.linked`.
    if (pendingSendsRef.current.length > 0) {
      const queued = pendingSendsRef.current;
      pendingSendsRef.current = [];
      for (const value of queued) {
        avatarEmbedRef.current?.sendText(value);
      }
    }
  }, []);

  // Model-load and live-connection failures both land here (NativeLiveAvatar
  // forwards its error boundary + liveConnectionError). Before this handler
  // existed a failed GLB fetch left a permanently faceless, silent badge.
  const handleEmbedError = useCallback(() => {
    setConnectionState('error');
    if (autoReconnectsRef.current >= MAX_AUTO_RECONNECTS || autoReconnectTimerRef.current) {
      return;
    }
    autoReconnectsRef.current += 1;
    const backoffMs = autoReconnectsRef.current * 4000;
    autoReconnectTimerRef.current = setTimeout(() => {
      autoReconnectTimerRef.current = null;
      reconnect();
    }, backoffMs);
    setEmbedError('Avatar connection hit a snag.');
  }, [reconnect]);

  useEffect(() => () => {
    if (autoReconnectTimerRef.current) {
      clearTimeout(autoReconnectTimerRef.current);
    }
  }, []);

  // STT engine selection (site config `asr`, 6 Aug 2026): route the mic
  // path through Camlin Speech ASR when configured — the SDK-global
  // registry is what `sttFromMic` dispatches on, so the mini-mic and the
  // live listen button both follow without prop threading. Default stays
  // Azure; see sttEngine.ts.
  useEffect(() => {
    configureSttEngine(config.asr);
  }, [config.asr]);

  useEffect(() => {
    if (consentAccepted && !readyEmitted.current) {
      readyEmitted.current = true;
      onReady?.();
    }
  }, [consentAccepted, onReady]);

  // Phone/tablet breakpoint tracking for the mobile takeover.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mql = window.matchMedia(PHONE_MEDIA_QUERY);
    const handleChange = (event: MediaQueryListEvent) => setPhoneViewport(event.matches);
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, []);

  // Desktop breakpoint tracking for the scaled-up launcher (round 6).
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mql = window.matchMedia(DESKTOP_MEDIA_QUERY);
    const handleChange = (event: MediaQueryListEvent) => setDesktopViewport(event.matches);
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, []);

  // Follow the host page's theme live — a mid-session toggle (page theme
  // switch or OS scheme change) restyles the panel without a reload.
  useEffect(() => watchHostTheme(setHostTheme), []);

  // Keep the greeting-refresh guard in sync with the transcript.
  useEffect(() => {
    conversationStartedRef.current = messages.some((message) => message.role === 'user');
  }, [messages]);

  // Lock page scroll behind the mobile takeover (restored on toggle/close).
  useEffect(() => {
    if (!(panelOpen && phoneViewport) || typeof document === 'undefined') {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [panelOpen, phoneViewport]);

  /**
   * The one audio-unlock path (must run inside a user gesture): resumes
   * the shared playback AudioContext + primes the SDK's audio elements via
   * the embed handle, then verifies the context actually ended up running
   * before declaring sound on. `persist` writes the soundConsent record so
   * later visits skip the ask and auto-unlock on the panel-open gesture.
   */
  const attemptSoundUnlock = useCallback(async (options: { persist: boolean }) => {
    try {
      await avatarEmbedRef.current?.unlockAudio();
    } catch {
      // Verified by the state check below — never trust a silent success.
    }

    const running = isPlaybackContextRunning();
    if (running) {
      setSoundOn(true);
      if (options.persist) {
        writeCompanionSoundConsent(storage);
      }
    }
    return running;
  }, [storage]);

  const openPanel = useCallback(() => {
    setPanelOpen(true);
    setNudge(null);
    // Returning visitor who already chose sound: the open click IS the
    // unlock gesture (browser policy still needs one per page load).
    if (!isPlaybackContextRunning() && isCompanionSoundEnabled(storage)) {
      void attemptSoundUnlock({ persist: false });
    }
  }, [attemptSoundUnlock, storage]);

  const handleEnableSound = useCallback(() => {
    void attemptSoundUnlock({ persist: true });
  }, [attemptSoundUnlock]);

  /**
   * Re-runs the SDK's browser-access flow after it surfaced a prompt
   * (blocked playback or mic). Success clears the prompt via
   * onBrowserAccessPromptChange; for the audio kind we also record the
   * unlock like an explicit enable.
   */
  const handleBrowserPromptRetry = useCallback(async () => {
    try {
      await avatarEmbedRef.current?.retryBrowserAccess();
    } catch {
      // Prompt stays visible; the visitor can retry.
    }
    if (isPlaybackContextRunning()) {
      setSoundOn(true);
      writeCompanionSoundConsent(storage);
    }
  }, [storage]);

  const handleBrowserAccessPromptChange = useCallback(
    (prompt: CompanionBrowserAccessPrompt | null) => {
      setBrowserPrompt(prompt);
      // A microphone prompt is a direct answer to the visitor's own mic
      // tap — if that tap came from the minimized launcher mic, open the
      // panel so the labelled retry affordance is actually visible.
      if (prompt?.kind === 'microphone') {
        setPanelOpen(true);
      }
    },
    []
  );

  const handleVoiceHintDismiss = useCallback(() => {
    setVoiceHintDismissed(true);
    dismissCompanionVoiceHint(storage);
  }, [storage]);

  // The SDK dispatches this window event for every bot reply on a live
  // contextual-chat session (see avatar-nextgen/src/core/hooks/
  // useLiveVisualChannel.ts `onPrompt`). Only attach the listener once
  // consent has been given and the live session can legitimately be open.
  useEffect(() => {
    if (!consentAccepted) {
      return undefined;
    }

    const handleChatContent = (event: Event) => {
      const detail = (event as CustomEvent).detail as
        | { text?: string; source?: string }
        | undefined;
      const action = chatActionFromAvatarChatContentEvent(detail);
      if (action) {
        setMessages((prev) => companionChatReducer(prev, action));
        setAwaitingReply(false);
        setConnectionState('ok');
      }
    };

    window.addEventListener('avatar:chat:content', handleChatContent);
    return () => window.removeEventListener('avatar:chat:content', handleChatContent);
  }, [consentAccepted]);

  // Voice-turn state mirrors: the live controller dispatches
  // `avatar:listening` around mic capture and `avatar:transcript` for
  // interim (isFinal: false) recognition text — the same events every
  // other live-mode consumer gets. Final transcripts arrive via the
  // embed's onSpeechInput prop (handleSpeechInput below).
  useEffect(() => {
    if (!consentAccepted) {
      return undefined;
    }

    const handleListening = (event: Event) => {
      const detail = (event as CustomEvent).detail as { listening?: boolean } | undefined;
      if (detail?.listening) {
        // Only honour listening=true for a turn THIS widget started (mic
        // tap → 'starting'). The SDK also dispatches `avatar:listening`
        // as an ambient "ready to listen" status around bot playback
        // (useLivePromptPlayback.finishLiveSpeaking) — mapping those into
        // micState locked the mic button in a disabled "Listening" state
        // the visitor never asked for (found live, 6 Aug 2026).
        micPendingGrantRef.current = false;
        setMicState((state) => (state === 'starting' ? 'listening' : state));
      } else {
        // While the capture layer says the mic is genuinely rolling,
        // ignore ambient listening=false — the runtime's Visual Channel
        // and playback-status dispatches flip this within ~1s of a turn
        // starting, which is exactly Keith's "listening bar shows for
        // about a second even though it's still listening" (round 5
        // item 2). Capture-truth events end the phase instead.
        if (captureActiveRef.current) {
          return;
        }
        setMicState((state) => (state === 'idle' ? state : 'idle'));
        setInterimTranscript('');
      }
    };

    // Capture-truth events from the Camlin ASR engine (round 5 item 2):
    // 'capturing' the instant samples are being buffered, 'processing'
    // when endpointing closed the window and recognition is in flight,
    // 'ended' when the turn finishes (any path). These outrank the
    // ambient avatar:listening chatter above. The Azure path doesn't
    // emit them, so its behaviour is unchanged.
    const handleMicCapture = (event: Event) => {
      const detail = (event as CustomEvent).detail as
        | { phase?: 'capturing' | 'processing' | 'ended' }
        | undefined;
      switch (detail?.phase) {
        case 'capturing':
          captureActiveRef.current = true;
          micPendingGrantRef.current = false;
          setMicState('listening');
          break;
        case 'processing':
          captureActiveRef.current = false;
          setVoiceProcessing(true);
          setMicState('idle');
          break;
        case 'ended':
          captureActiveRef.current = false;
          setVoiceProcessing(false);
          setMicState((state) => (state === 'idle' ? state : 'idle'));
          setInterimTranscript('');
          break;
        default:
          break;
      }
    };

    const handleTranscript = (event: Event) => {
      const detail = (event as CustomEvent).detail as
        | { text?: string; isFinal?: boolean }
        | undefined;
      if (typeof detail?.text !== 'string') {
        return;
      }
      if (detail.isFinal) {
        setInterimTranscript('');
      } else {
        setInterimTranscript(detail.text);
      }
    };

    // Item 9 (6 Aug 2026): the SDK keeps the voice turn armed while the
    // browser's mic permission dialog is up and announces it here. Mark
    // the pending grant (so handleMicTap's finally doesn't reset the
    // button to idle) and hold 'starting' — the strip keeps saying
    // "Starting microphone…", and when the visitor hits Allow the resumed
    // capture's listening=true lands on a 'starting' state and
    // transitions normally. A 60s valve un-sticks the button if the
    // visitor never answers the dialog (a grant after that still starts
    // capture — the listening event re-syncs the UI).
    let pendingValveTimer: ReturnType<typeof setTimeout> | null = null;
    const handleVoiceTurnPending = () => {
      micPendingGrantRef.current = true;
      setMicState((state) => (state === 'idle' ? 'starting' : state));
      if (pendingValveTimer) {
        clearTimeout(pendingValveTimer);
      }
      pendingValveTimer = setTimeout(() => {
        if (micPendingGrantRef.current) {
          micPendingGrantRef.current = false;
          setMicState((state) => (state === 'starting' ? 'idle' : state));
        }
      }, 60000);
    };

    window.addEventListener('avatar:listening', handleListening);
    window.addEventListener('avatar:mic-capture', handleMicCapture);
    window.addEventListener('avatar:transcript', handleTranscript);
    window.addEventListener('avatar:voice-turn-pending', handleVoiceTurnPending);
    return () => {
      if (pendingValveTimer) {
        clearTimeout(pendingValveTimer);
      }
      window.removeEventListener('avatar:listening', handleListening);
      window.removeEventListener('avatar:mic-capture', handleMicCapture);
      window.removeEventListener('avatar:transcript', handleTranscript);
      window.removeEventListener('avatar:voice-turn-pending', handleVoiceTurnPending);
    };
  }, [consentAccepted]);

  // Round 5 item 6: tall and half modes PUSH the host page (body margin,
  // classic side-drawer embed) so the site stays fully viewable in its own
  // region instead of being overlaid. Restores the body's inline styles on
  // any exit path (mode change, panel close, unmount, phone viewport).
  const halfWidth = `${(clampHalfRatio(halfRatio) * 100).toFixed(2)}vw`;
  useEffect(() => {
    if (!panelOpen || phoneViewport || panelMode === 'float') {
      return undefined;
    }
    const width = panelMode === 'half'
      ? halfWidth
      : `calc(${TALL_PANEL_WIDTH} + ${EDGE_GAP_PX * 2}px)`;
    return pushHostPage(width);
  }, [panelOpen, phoneViewport, panelMode, halfWidth]);

  // Round 5 item 7: half-mode divider drag. Pointer capture keeps the
  // move/up stream on the divider even when the pointer crosses the
  // avatar canvas or the host page.
  const handleDividerPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    dividerDraggingRef.current = true;
  }, []);

  const handleDividerPointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!dividerDraggingRef.current || typeof window === 'undefined') {
      return;
    }
    setHalfRatio(clampHalfRatio((window.innerWidth - event.clientX) / window.innerWidth));
  }, []);

  const handleDividerPointerUp = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!dividerDraggingRef.current) {
      return;
    }
    dividerDraggingRef.current = false;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      /* capture already released */
    }
    if (typeof window !== 'undefined') {
      const ratio = clampHalfRatio((window.innerWidth - event.clientX) / window.innerWidth);
      setHalfRatio(ratio);
      writeCompanionHalfRatio(storage, ratio);
    }
  }, [storage]);

  // Keep the newest message (and the thinking indicator) in view. Without
  // this, replies past the first exchange rendered below the fold of the
  // fixed-height transcript and the widget LOOKED dead — Keith's "responds
  // once or twice then goes quiet" (5 Aug 2026): the runtime answered every
  // turn (CloudWatch outcome=answered), the panel just never scrolled.
  useEffect(() => {
    const el = transcriptRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages, awaitingReply, panelOpen, interimTranscript]);

  // A sent turn that stays unanswered past the timeout surfaces a stalled
  // status with a manual reconnect — never silence.
  useEffect(() => {
    if (!awaitingReply) {
      return undefined;
    }

    const timer = setTimeout(() => {
      setConnectionState((state) => (state === 'ok' ? 'stalled' : state));
    }, REPLY_STALL_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [awaitingReply]);

  // Voice phase "done" flash (round 4, item 8): when a voice turn's reply
  // lands, show the green done state briefly before returning to idle.
  useEffect(() => {
    if (awaitingReply || !lastTurnVoiceRef.current) {
      return undefined;
    }
    lastTurnVoiceRef.current = false;
    setVoiceDoneFlash(true);
    const timer = setTimeout(() => setVoiceDoneFlash(false), 1800);
    return () => clearTimeout(timer);
  }, [awaitingReply]);

  /**
   * Real mic level meter (round 4, item 8). While listening, tap the
   * shared mic stream the SDK cached at permission time and drive the
   * strip's level bars from actual RMS — direct DOM writes via ref, no
   * re-render per frame. When no stream is reachable the bars keep their
   * CSS fallback animation, so the state is still unmistakable.
   */
  const levelBarsRef = useRef<HTMLSpanElement | null>(null);
  useEffect(() => {
    if (micState !== 'listening' || typeof window === 'undefined') {
      return undefined;
    }
    const w = window as unknown as {
      camlinSharedAudioStream?: MediaStream | null;
      currentAudioStream?: MediaStream | null;
      AudioContext?: typeof AudioContext;
      webkitAudioContext?: typeof AudioContext;
    };
    const stream = w.camlinSharedAudioStream ?? w.currentAudioStream;
    const Ctx = w.AudioContext ?? w.webkitAudioContext;
    if (!(stream instanceof MediaStream) || !stream.active || !Ctx) {
      return undefined;
    }

    let ctx: AudioContext;
    let source: MediaStreamAudioSourceNode;
    let analyser: AnalyserNode;
    try {
      ctx = new Ctx();
      source = ctx.createMediaStreamSource(stream);
      analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.5;
      source.connect(analyser);
    } catch {
      return undefined;
    }

    const data = new Uint8Array(analyser.frequencyBinCount);
    let raf = 0;
    const tick = () => {
      analyser.getByteTimeDomainData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i += 1) {
        const v = (data[i] - 128) / 128;
        sum += v * v;
      }
      const level = Math.min(1, Math.sqrt(sum / data.length) * 5);
      const bars = levelBarsRef.current?.children;
      if (bars) {
        for (let i = 0; i < bars.length; i += 1) {
          const bar = bars[i] as HTMLElement;
          // Middle bars react hardest — reads as a voice waveform.
          const emphasis = 1 - Math.abs(i - (bars.length - 1) / 2) / bars.length;
          bar.style.animation = 'none';
          bar.style.transform = `scaleY(${Math.max(0.2, level * (0.5 + emphasis)).toFixed(3)})`;
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      try {
        source.disconnect();
        void ctx.close();
      } catch {
        // Best-effort teardown.
      }
    };
  }, [micState]);

  // CO2 (doc 116 §7 D5) — the first-party consent record the visitor set
  // doesn't spontaneously change, but a SITE's own consent gate
  // (`window.__camlinCompanionSiteConsent`) can close at any time (e.g. a
  // cookie-consent banner the visitor later revokes). `isCompanionInert`
  // is already recomputed fresh on every render below; this periodic
  // check is what actually TRIGGERS a re-render when nothing else would.
  useEffect(() => {
    if (!consentAccepted) {
      return undefined;
    }

    const interval = setInterval(() => {
      if (isCompanionInert(storage)) {
        setConsentAccepted(false);
        onConsentChange?.(false);
      }
    }, SITE_CONSENT_GATE_POLL_MS);

    return () => clearInterval(interval);
  }, [consentAccepted, storage, onConsentChange]);

  // CO2 (doc 116 §2.1.2) — dispatches the bridge's page-context digest on
  // mount and on every SPA navigation via the pre-existing
  // `avatar:live:contextUpdate` window event (Team Mode's TM-2 mechanism,
  // `useLiveListenControls.ts`), which forwards straight to
  // `LiveModeClient.sendSessionContext` with no metadata allowlist
  // filtering — see `pageContext.ts`'s file header for why this path was
  // chosen over the live-update metadata builder.
  useEffect(() => {
    if (!consentAccepted) {
      return undefined;
    }

    // The watcher's first onChange is the mount-time capture, not a real
    // navigation — the session-open greeting (item 3's seeded pageContext)
    // already covers that page, so the refresh below must skip it.
    let initialCapture = true;

    const handle = watchPageContext({
      onChange: (context) => {
        window.dispatchEvent(
          new CustomEvent('avatar:live:contextUpdate', { detail: { pageContext: context } })
        );
        try {
          setPagePath(new URL(context.url).pathname);
        } catch {
          // Malformed URL — extremely unlikely for window.location.href.
          // Keep the previous pagePath rather than crashing the watcher.
        }

        const isNavigation = !initialCapture;
        initialCapture = false;

        // Round 6 item 5 — SPA-navigation greeting refresh: while the
        // conversation hasn't begun, swap the pending greeting for the
        // new page's one (live /generate/companion-greeting; fail-soft).
        // The reducer re-checks the no-user-turn guard at apply time, so
        // a turn racing this fetch can never rewrite real history.
        if (isNavigation && !conversationStartedRef.current) {
          void fetchCompanionGreeting({
            siteKey: config.siteKey,
            pageContext: context,
            baseUrl: config.companionApiUrl
          }).then((greeting) => {
            if (greeting && !conversationStartedRef.current) {
              setMessages((prev) => companionChatReducer(prev, { type: 'greeting_refresh', text: greeting }));
            }
          });
        }
      }
    });

    return () => handle.stop();
  }, [consentAccepted, config.siteKey, config.companionApiUrl]);

  // CO2 (doc 116 §2.4/§7 D3) — real dwell/scroll/exit-intent detectors,
  // re-armed for the current page on every SPA navigation (`pagePath`
  // change). A fired trigger is evaluated against BOTH the local caps and
  // the server's independent copy (`proactiveOrchestrator.ts`) before the
  // nudge bubble shows.
  useEffect(() => {
    if (!consentAccepted || !config.proactive.caps.enabled || config.proactive.triggers.length === 0) {
      return undefined;
    }

    const handle = watchProactiveTriggers({
      triggers: config.proactive.triggers,
      pagePath,
      onTriggerMatched: (trigger) => {
        void (async () => {
          const visitorId = visitorIdRef.current ?? getOrCreateCompanionVisitorId(storage);
          visitorIdRef.current = visitorId;

          const result = await evaluateAndShowProactiveNudge({
            pagePath,
            caps: config.proactive.caps,
            session: proactiveSessionRef.current,
            storage,
            engagementClient: engagementClientRef.current,
            siteKey: config.siteKey,
            visitorId,
            // `onTriggerMatched` only fires when `watchProactiveTriggers`
            // has already confirmed a real DOM signal (dwell elapsed,
            // scroll past threshold, or exit-intent) matched this trigger.
            triggerMatched: true
          });

          if (result.allowed) {
            proactiveSessionRef.current = result.nextSession;
            setNudge({ trigger });
          }
        })();
      }
    });

    return () => handle.stop();
  }, [consentAccepted, pagePath, config.proactive, config.siteKey, storage]);

  /** Send now if the embed is live, otherwise park in pendingSendsRef (see its doc). The optimistic bubble is added by the caller either way. */
  const dispatchOrQueueSend = useCallback((value: string) => {
    if (avatarReady) {
      avatarEmbedRef.current?.sendText(value);
      return;
    }
    pendingSendsRef.current.push(value);
  }, [avatarReady]);

  const handleSend = useCallback(() => {
    const value = inputValue.trim();
    if (!value) {
      return;
    }

    dispatchOrQueueSend(value);
    setMessages((prev) => companionChatReducer(prev, { type: 'user_sent', text: value }));
    setInputValue('');
    setNudge(null);
    lastTurnVoiceRef.current = false;
    setAwaitingReply(true);
  }, [inputValue, dispatchOrQueueSend]);

  /**
   * Final voice transcript from the SDK's live-mode capture. Lands in the
   * chat thread exactly like a typed turn: `handleInputExternally` on the
   * live config keeps the SDK from sending it itself, so the widget owns
   * the single send (no double turns) and the optimistic bubble.
   */
  const handleSpeechInput = useCallback((text: string) => {
    const value = text.trim();
    if (!value) {
      return;
    }

    dispatchOrQueueSend(value);
    setMessages((prev) => companionChatReducer(prev, { type: 'user_sent', text: value }));
    setInterimTranscript('');
    setMicState('idle');
    setNudge(null);
    lastTurnVoiceRef.current = true;
    setAwaitingReply(true);
  }, [dispatchOrQueueSend]);

  /**
   * Mic button tap — the canonical live-mode voice turn. The tap is the
   * browser gesture for BOTH permissions: mic (getUserMedia runs inside
   * the SDK's primary action; first tap prompts, per Keith never on load)
   * and audio playback (unlocked here so this turn's spoken reply is
   * audible even on a first visit). Single-turn capture: recognition stops
   * after one phrase or 3s silence; while live, the button pulses and the
   * caption + interim bubble show what's being heard.
   */
  const handleMicTap = useCallback(() => {
    if (micState !== 'idle' || !avatarReady) {
      return;
    }

    setMicState('starting');
    setInterimTranscript('');
    setNudge(null);
    void (async () => {
      try {
        // Sequenced, not parallel: two concurrent unlockAudio calls (this
        // one + the one inside the SDK's browser-access check) used to race
        // on the audio element's muted flag and leave TTS permanently muted
        // (silent-speech defect, root-caused 6 Aug 2026). unlockAudio is
        // also serialized SDK-side now; the sequencing keeps intent clear.
        // Round 5 (tap→capture latency): skip the unlock dance entirely
        // when the playback context is already running — it was costing
        // hundreds of ms per tap for visitors whose sound is already on
        // (the SDK's access check still unlocks on the same gesture).
        if (!soundOn || !isPlaybackContextRunning()) {
          await attemptSoundUnlock({ persist: true });
        }
        await avatarEmbedRef.current?.startVoiceTurn();
      } finally {
        // If capture actually started, `avatar:listening` already moved us
        // to 'listening' before this resolves (the dispatch precedes the
        // capture await). Still 'starting' here means the attempt died
        // (mic denied → the SDK's prompt surfaces below; token/STT error →
        // the SDK sets its connection error) — reset the button. EXCEPT
        // when the browser permission dialog is still outstanding (item 9,
        // micPendingGrantRef): the turn is armed SDK-side and resumes on
        // Allow, so the button must stay in 'starting'.
        setMicState((state) => (state === 'starting' && !micPendingGrantRef.current ? 'idle' : state));
      }
    })();
  }, [micState, avatarReady, soundOn, attemptSoundUnlock]);

  const handleNudgeOpen = useCallback(() => {
    openPanel();
  }, [openPanel]);

  const handleNudgeDismiss = useCallback(() => {
    setNudge(null);
    recordProactiveDismiss(storage);
    const visitorId = visitorIdRef.current;
    if (visitorId) {
      void engagementClientRef.current.dismiss({ siteKey: config.siteKey, visitorId });
    }
  }, [storage, config.siteKey]);

  if (inert) {
    const priorChoice = readCompanionConsent(storage)?.choice;
    if (priorChoice === 'declined') {
      return null;
    }

    return (
      <CompanionConsentPrompt
        personaName={config.persona.displayName}
        siteName={config.siteName}
        onAccept={() => {
          writeCompanionConsent(storage, 'accepted');
          setConsentAccepted(true);
          onConsentChange?.(true);
        }}
        onDecline={() => {
          writeCompanionConsent(storage, 'declined');
          onConsentChange?.(false);
        }}
      />
    );
  }

  const launcherLabel = config.avatar.launcherLabel ?? `Chat with ${config.persona.displayName}`;
  const showStageOverlay = !avatarReady || connectionState === 'error' || connectionState === 'reconnecting';
  const stageOverlayText = connectionState === 'error'
    ? (embedError ?? 'Avatar unavailable')
    : connectionState === 'reconnecting'
      ? 'Reconnecting…'
      : 'Loading avatar…';
  const showManualReconnect =
    connectionState === 'stalled'
    || (connectionState === 'error' && autoReconnectsRef.current >= MAX_AUTO_RECONNECTS && !autoReconnectTimerRef.current);
  const palette = paletteFor(hostTheme);
  const launcher = launcherGeometry(desktopViewport && !phoneViewport);
  const audioPromptActive = browserPrompt?.kind === 'audio';
  const micPromptActive = browserPrompt?.kind === 'microphone';
  const showSoundBanner = !soundOn && !audioPromptActive;
  const micListening = micState === 'listening';
  const micActive = micListening || micState === 'starting';
  // Voice turn phase for the state strip (round 4, item 8).
  const voicePhase: CompanionVoicePhase = micListening
    ? 'listening'
    : micState === 'starting'
      ? 'starting'
      : voiceProcessing || (awaitingReply && lastTurnVoiceRef.current)
        ? 'processing'
        : voiceDoneFlash
          ? 'done'
          : 'idle';
  const voicePhaseLabel = voicePhase === 'listening'
    ? 'Listening — speak now'
    : voicePhase === 'starting'
      ? 'Starting microphone…'
      : voicePhase === 'processing'
        ? 'Processing what you said…'
        : 'Got it';
  // The static hint is dismissible + persisted; live voice states render
  // in the dedicated state strip above the input row.
  const showVoiceCaption = voicePhase === 'idle' && !voiceHintDismissed;

  return (
    <>
      <style>{WIDGET_KEYFRAMES}</style>

      {/*
        The avatar stage: mounts the real avatar SDK widget (AvatarEmbed,
        native mode) and opens the live contextual-chat session
        (host='companion') as soon as consent is accepted. Always mounted —
        collapsed it's the launcher badge's face, open it's the panel's face
        pane — so the session stays warm across open/close AND across the
        mobile Website/Avatar toggle. `key` bumps only on deliberate
        reconnect/retry. The inner wrapper exists in both modes (style-only
        changes) so React never remounts the embed.
      */}
      <div {...{ [CAMLIN_COMPANION_ATTR.badge]: 'stage' }} style={buildStageStyle(panelOpen, phoneViewport, panelMode, launcher, halfWidth)}>
        <div style={buildStageInnerStyle(panelOpen, launcher)}>
          <AvatarEmbed
            key={`companion-embed-${embedEpoch}`}
            ref={avatarEmbedRef}
            config={avatarEmbedConfig}
            style={stageEmbedStyle}
            onReady={handleEmbedReady}
            onError={handleEmbedError}
            onSpeechInput={handleSpeechInput}
            onBrowserAccessPromptChange={handleBrowserAccessPromptChange}
          />
        </div>
        {showStageOverlay && (
          <div style={stageOverlayStyle}>
            <div className="camlin-companion-spinner" aria-hidden="true" />
            {panelOpen && <div>{stageOverlayText}</div>}
          </div>
        )}
        {/*
          Round 3: title + close as a scrim strip over the top of the face
          pane instead of their own panel row — the freed height belongs
          to the chat. Phone takeover keeps its Website|Avatar bar; the
          strip there carries the title only.
        */}
        {panelOpen && (
          <div {...{ [CAMLIN_COMPANION_ATTR.panel]: 'header-overlay' }} style={headerOverlayStyle}>
            <span>{config.persona.displayName}</span>
            {!phoneViewport && (
              <span style={{ display: 'flex', gap: '6px', pointerEvents: 'auto' }}>
                {/*
                  Size modes (round 4, item 10): maximize = full-height side
                  panel; half = avatar right half / website left half.
                  Tapping the active mode returns to the floating panel.
                  Style-only switches — session + transcript preserved.
                */}
                <button
                  type="button"
                  {...{ [CAMLIN_COMPANION_ATTR.panel]: 'mode-tall' }}
                  aria-label={panelMode === 'tall' ? 'Back to floating panel' : 'Maximize to full-height panel'}
                  title={panelMode === 'tall' ? 'Back to floating panel' : 'Maximize to full-height panel'}
                  aria-pressed={panelMode === 'tall'}
                  style={buildHeaderModeButtonStyle(panelMode === 'tall')}
                  onClick={() => setPanelMode((mode) => (mode === 'tall' ? 'float' : 'tall'))}
                >
                  ⤢
                </button>
                <button
                  type="button"
                  {...{ [CAMLIN_COMPANION_ATTR.panel]: 'mode-half' }}
                  aria-label={panelMode === 'half' ? 'Back to floating panel' : 'Half-screen mode'}
                  title={panelMode === 'half' ? 'Back to floating panel' : 'Half-screen mode'}
                  aria-pressed={panelMode === 'half'}
                  style={buildHeaderModeButtonStyle(panelMode === 'half')}
                  onClick={() => setPanelMode((mode) => (mode === 'half' ? 'float' : 'half'))}
                >
                  ◧
                </button>
                <button
                  type="button"
                  aria-label="Close chat"
                  style={headerCloseStyle}
                  onClick={() => setPanelOpen(false)}
                >
                  ✕
                </button>
              </span>
            )}
          </div>
        )}
        {/*
          Chest mic (round 6, Keith item 2): the primary voice affordance
          lives INSIDE the face pane, overlaid on the avatar's chest —
          below the face, never over it. Same voice turn as everywhere
          else; the button wears the capture-truth phase colours and the
          slimmed strip above the input row keeps the labelled state +
          live level bars.
        */}
        {panelOpen && (
          <button
            type="button"
            {...{ [CAMLIN_COMPANION_ATTR.panel]: 'chest-mic' }}
            className={micListening ? 'camlin-companion-mic-live' : undefined}
            style={buildChestMicStyle(voicePhase, micState === 'idle' && avatarReady)}
            aria-label={voicePhase === 'idle' ? 'Speak your message' : voicePhaseLabel}
            title={voicePhase === 'idle' ? 'Speak your message' : voicePhaseLabel}
            disabled={micState !== 'idle' || !avatarReady}
            onClick={handleMicTap}
          >
            <MicIcon size={24} />
          </button>
        )}
      </div>

      {!panelOpen && (
        <>
          {/*
            Round 4 (Keith item 5): the launcher is a rounded RECTANGLE —
            face area on top (the stage element above), "Chat with avatar"
            strip underneath inside the same rect. The old side pill is
            gone; the invitation text lives on the launcher itself.
          */}
          <div {...{ [CAMLIN_COMPANION_ATTR.badge]: 'label' }} style={buildLauncherTextStripStyle(launcher)} aria-hidden="true">
            Chat with avatar
          </div>
          <button
            type="button"
            aria-label={launcherLabel}
            {...{ [CAMLIN_COMPANION_ATTR.badge]: 'launcher' }}
            style={buildLauncherButtonStyle(launcher)}
            onClick={openPanel}
          />
          {/*
            Thinking indicator while minimized (round 4, item 7): the same
            pulsing-dots effect the chat bubble uses, as a bubble beside
            the launcher — near the face, never over it.
          */}
          {awaitingReply && connectionState === 'ok' && (
            <div
              {...{ [CAMLIN_COMPANION_ATTR.badge]: 'thinking' }}
              style={buildLauncherThinkingStyle(palette, launcher)}
              role="status"
              aria-label={`${config.persona.displayName} is thinking`}
            >
              <span className="camlin-companion-dot">●</span>{' '}
              <span className="camlin-companion-dot">●</span>{' '}
              <span className="camlin-companion-dot">●</span>
            </div>
          )}
          {/*
            Talk while minimized (round 3): same voice turn as the panel's
            mic button — replies play aloud, the transcript accumulates
            for whenever the panel opens. Sits ABOVE the rect; never
            over the face.
          */}
          <button
            type="button"
            {...{ [CAMLIN_COMPANION_ATTR.badge]: 'mini-mic' }}
            className={micListening ? 'camlin-companion-mic-live' : undefined}
            style={buildMiniMicStyle(palette, micListening, micState === 'idle' && avatarReady, launcher)}
            aria-label={micListening ? 'Listening — speak now' : 'Speak to our avatar'}
            title={micListening ? 'Listening — speak now' : 'Speak to our avatar'}
            disabled={micState !== 'idle' || !avatarReady}
            onClick={handleMicTap}
          >
            <MicIcon size={17} />
          </button>
          {audioPromptActive && (
            <div
              {...{ [CAMLIN_COMPANION_ATTR.badge]: 'muted-dot' }}
              style={mutedDotStyle}
              title="Sound is off — open the chat to enable it"
            >
              <span aria-hidden="true">🔇</span>
            </div>
          )}
        </>
      )}

      {nudge && !panelOpen && (
        <CompanionNudgeBubble
          message={nudge.trigger.nudgeMessage ?? `Want a hand with ${config.persona.displayName}?`}
          onOpen={handleNudgeOpen}
          onDismiss={handleNudgeDismiss}
        />
      )}

      {/* Round 5 item 7: the draggable boundary between website and avatar
          panel in half mode. Sits exactly on the panel's left edge; drag
          resizes both regions (the push effect follows halfWidth live) and
          the release persists the chosen ratio. */}
      {panelOpen && !phoneViewport && panelMode === 'half' && (
        <div
          {...{ [CAMLIN_COMPANION_ATTR.badge]: 'divider' }}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize avatar panel"
          onPointerDown={handleDividerPointerDown}
          onPointerMove={handleDividerPointerMove}
          onPointerUp={handleDividerPointerUp}
          onPointerCancel={handleDividerPointerUp}
          style={{
            position: 'fixed',
            top: 0,
            bottom: 0,
            right: halfWidth,
            width: '12px',
            marginRight: '-6px',
            cursor: 'col-resize',
            zIndex: 10002,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            touchAction: 'none',
            background: 'transparent'
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: '4px',
              height: '48px',
              borderRadius: '2px',
              background: palette.text,
              opacity: 0.35
            }}
          />
        </div>
      )}

      {panelOpen && (
        <div
          {...{ [CAMLIN_COMPANION_ATTR.panel]: 'chat' }}
          {...{ [CAMLIN_COMPANION_ATTR.shell]: 'panel' }}
          style={buildPanelStyle(phoneViewport, palette, panelMode, halfWidth)}
        >
          {/*
            Mobile takeover top bar: "Website | Avatar" segmented toggle.
            Website minimizes back to the page (session + transcript stay
            warm — the stage never unmounts); Avatar is this view.
          */}
          {phoneViewport && (
            <div {...{ [CAMLIN_COMPANION_ATTR.panel]: 'takeover-bar' }} style={buildTakeoverBarStyle(palette)}>
              <div style={buildSegmentWrapStyle(palette)} role="tablist" aria-label="View">
                <button
                  type="button"
                  role="tab"
                  aria-selected={false}
                  style={buildSegmentStyle(false, palette)}
                  onClick={() => setPanelOpen(false)}
                >
                  Website
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected
                  style={buildSegmentStyle(true, palette)}
                >
                  Avatar
                </button>
              </div>
            </div>
          )}

          {/* The avatar stage overlays this region — see buildStageStyle. */}
          <div
            style={{ height: phoneViewport ? PHONE_FACE_HEIGHT : panelFaceHeight(panelMode), flex: 'none' }}
            aria-hidden="true"
          />

          <div style={panelBodyStyle}>
            {/*
              Round 3: no header row here — the title + close moved into
              the headerOverlay strip over the face pane; this whole row's
              height now belongs to the chat.

            */}
            {/*
              First-visit sound unlock: a real labelled button (Keith,
              6 Aug 2026 — "a real affordance, not an icon mystery").
              Hidden once sound is on; superseded by the muted chip below
              when the SDK reports actively blocked playback.
            */}
            {showSoundBanner && (
              <button
                type="button"
                {...{ [CAMLIN_COMPANION_ATTR.panel]: 'enable-sound' }}
                style={soundBannerStyle}
                onClick={handleEnableSound}
              >
                <span aria-hidden="true">🔊</span>
                <span>Enable sound — hear replies out loud</span>
              </button>
            )}

            <div ref={transcriptRef} style={transcriptStyle}>
              {messages.length === 0 && !awaitingReply && (
                <p style={{ margin: 0, opacity: 0.7 }}>
                  {avatarReady ? 'Ask me anything about this site.' : 'Connecting…'}
                </p>
              )}
              {messages.map((message) => (
                <div
                  key={message.id}
                  style={message.role === 'user' ? buildUserBubbleStyle(palette) : buildAssistantBubbleStyle(palette)}
                >
                  {/*
                    Avatar replies render markdown (round 3) — React
                    elements only, no innerHTML, links open a new tab.
                    User turns stay literal text.
                  */}
                  {message.role === 'user' ? message.text : renderCompanionMarkdown(message.text)}
                </div>
              ))}
              {micListening && interimTranscript && (
                <div style={buildInterimBubbleStyle(palette)} aria-label="Heard so far">
                  {interimTranscript}
                </div>
              )}
              {awaitingReply && connectionState === 'ok' && (
                <div style={buildAssistantBubbleStyle(palette)} aria-label={`${config.persona.displayName} is thinking`}>
                  <span className="camlin-companion-dot">●</span>{' '}
                  <span className="camlin-companion-dot">●</span>{' '}
                  <span className="camlin-companion-dot">●</span>
                </div>
              )}
            </div>

            {connectionState !== 'ok' && (
              <div style={buildStatusLineStyle(palette)} role="status">
                <span style={{ flex: 1 }}>
                  {connectionState === 'stalled' && 'No reply yet — the connection may have dropped.'}
                  {connectionState === 'reconnecting' && 'Reconnecting…'}
                  {connectionState === 'error' && 'Connection lost.'}
                </span>
                {showManualReconnect && (
                  <button type="button" style={buildStatusActionStyle(palette)} onClick={reconnect}>
                    Reconnect
                  </button>
                )}
              </div>
            )}

            {/*
              Browser blocked audio playback mid-session (autoplay policy
              rejected a reply's TTS): visible muted chip, un-mutes on tap.
              Never a silent play-fail.
            */}
            {audioPromptActive && (
              <div
                {...{ [CAMLIN_COMPANION_ATTR.panel]: 'muted-chip' }}
                style={buildStatusLineStyle(palette)}
                role="status"
              >
                <span style={{ flex: 1 }}>
                  <span aria-hidden="true">🔇</span> Sound is muted by the browser.
                </span>
                <button
                  type="button"
                  style={buildStatusActionStyle(palette)}
                  onClick={() => { void handleBrowserPromptRetry(); }}
                >
                  Enable sound
                </button>
              </div>
            )}

            {/* Mic permission ask — a labelled button inside the panel, never floating over the face. */}
            {micPromptActive && (
              <div
                {...{ [CAMLIN_COMPANION_ATTR.panel]: 'mic-chip' }}
                style={buildStatusLineStyle(palette)}
                role="status"
              >
                <span style={{ flex: 1 }}>{browserPrompt?.message ?? 'Microphone access is blocked.'}</span>
                <button
                  type="button"
                  style={buildStatusActionStyle(palette)}
                  onClick={() => { void handleBrowserPromptRetry(); }}
                >
                  Allow microphone
                </button>
              </div>
            )}

            {/*
              Voice turn state strip (round 4, item 8): unmistakable state,
              not a subtle pulse — starting / listening (red, LIVE level
              bars from the mic stream) / processing (amber, spinner) /
              done (green flash), each labelled.
            */}
            {voicePhase !== 'idle' && (
              <div
                {...{ [CAMLIN_COMPANION_ATTR.panel]: 'voice-state' }}
                data-voice-phase={voicePhase}
                style={buildVoiceStateStripStyle(voicePhase, palette)}
                role="status"
              >
                {voicePhase === 'listening' && (
                  <span ref={levelBarsRef} style={voiceLevelBarsStyle} aria-hidden="true">
                    <span className="camlin-companion-level-bar" />
                    <span className="camlin-companion-level-bar" />
                    <span className="camlin-companion-level-bar" />
                    <span className="camlin-companion-level-bar" />
                    <span className="camlin-companion-level-bar" />
                  </span>
                )}
                {voicePhase === 'processing' && <span className="camlin-companion-spinner" aria-hidden="true" style={{ width: '13px', height: '13px' }} />}
                {voicePhase === 'done' && <span aria-hidden="true">✓</span>}
                <span style={{ flex: 1 }}>{voicePhaseLabel}</span>
              </div>
            )}

            <div style={inputRowStyle}>
              <input
                type="text"
                style={buildInputStyle(palette)}
                value={inputValue}
                placeholder="Type a message…"
                aria-label="Message"
                onChange={(event) => setInputValue(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    handleSend();
                  }
                }}
              />
              {/*
                Round 6: the input-row mic moved to the chest of the avatar
                (see the chest-mic button in the stage) — the row is text
                input + Send only now.
              */}
              <button type="button" style={sendButtonStyle} onClick={handleSend}>
                Send
              </button>
            </div>

            {/*
              Dismissible hint (round 3): the × persists the dismissal.
              Live voice states render in the state strip above, so this
              is idle-only guidance now.
            */}
            {showVoiceCaption && (
              <p style={buildVoiceCaptionStyle(palette)} {...{ [CAMLIN_COMPANION_ATTR.panel]: 'voice-caption' }}>
                <span>Replies are spoken aloud — tap the mic to talk.</span>
                <button
                  type="button"
                  {...{ [CAMLIN_COMPANION_ATTR.panel]: 'voice-caption-dismiss' }}
                  style={buildHintDismissStyle(palette)}
                  aria-label="Dismiss this hint"
                  title="Dismiss this hint"
                  onClick={handleVoiceHintDismiss}
                >
                  ✕
                </button>
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
}

let companionHostCounter = 0;

function createCompanionHostId(): string {
  companionHostCounter += 1;
  return `camlin-companion-host-${companionHostCounter}`;
}

/**
 * Mount the companion widget into a fixed-position host element (no layout shift).
 * Mirrors CamlinAvatarElement shadow-root mount path from src/sdk/widget/AvatarWidget.tsx.
 */
export function mountCompanionWidget(
  config: CompanionSiteConfig,
  options?: { storage?: CompanionConsentStorage; onReady?: () => void }
): { host: HTMLElement; unmount: () => void } {
  const host = document.createElement('div');
  host.id = createCompanionHostId();
  host.setAttribute(CAMLIN_COMPANION_ATTR.shell, 'root');
  host.style.position = 'fixed';
  host.style.inset = '0';
  host.style.pointerEvents = 'none';
  host.style.zIndex = '9997';
  // Children re-enable pointer events
  document.body.appendChild(host);

  const mountPoint = document.createElement('div');
  mountPoint.style.pointerEvents = 'auto';
  host.appendChild(mountPoint);

  const root = createRoot(mountPoint);
  root.render(
    React.createElement(CompanionWidget, {
      config,
      storage: options?.storage,
      onReady: options?.onReady
    })
  );

  return {
    host,
    unmount: () => {
      root.unmount();
      host.remove();
    }
  };
}

/**
 * Custom element wrapper for script-tag integration (parallel to camlin-avatar).
 */
export class CamlinCompanionElement extends HTMLElement {
  private root: ReturnType<typeof createRoot> | null = null;
  private container: HTMLDivElement | null = null;
  private unmountWidget: (() => void) | null = null;

  connectedCallback() {
    const configJson = this.getAttribute('data-config');
    if (!configJson) {
      console.warn('[Camlin Companion] data-config attribute required on <camlin-companion>');
      return;
    }

    try {
      const config = JSON.parse(configJson) as CompanionSiteConfig;
      const shadow = this.shadowRoot || this.attachShadow({ mode: 'open' });

      if (!this.container) {
        this.container = document.createElement('div');
        this.container.setAttribute(CAMLIN_COMPANION_ATTR.shell, 'custom-element');
      }

      if (!shadow.contains(this.container)) {
        shadow.appendChild(this.container);
      }

      if (!this.root) {
        this.root = createRoot(this.container);
      }

      this.root.render(React.createElement(CompanionWidget, { config }));
    } catch (error) {
      console.error('[Camlin Companion] failed to parse data-config', error);
    }
  }

  disconnectedCallback() {
    this.unmountWidget?.();
    this.unmountWidget = null;
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
  }
}

if (
  typeof window !== 'undefined' &&
  typeof customElements !== 'undefined' &&
  !customElements.get('camlin-companion')
) {
  customElements.define('camlin-companion', CamlinCompanionElement);
}

export default CompanionWidget;
